import apis from "../../utils/apis";
import {
  FETCH_TASKS,
  FETCH_TASK_DETAILS,
  CREATE_TASK_SUCCESS,
  ASSIGN_TASK_SUCCESS,
  UPDATE_TASK_SUCCESS,
  DELETE_TASK_SUCCESS,
  COMPLETE_TASK_SUCCESS,
  FETCH_TASK_PREFERENCES,
  UPDATE_TASK_PREFERENCES,
  AppThunk,
} from "../types";
import { Task } from "../../components/Tasks/types";
import { showFeedbackModal } from "./UserFeedbackActions";
import { handleApiError } from "./utils/utils";
import { translate } from '../../i18n/translate';

interface FilterOption {
  id: number;
  name: string;
  uuid: string;
}

interface FetchTasksResponse {
  tasks: Task[];
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  filter_options?: FilterOption[];
}

interface TaskListParams {
  page?: number;
  limit?: number;
  matter_id?: number;
  matter_uuid?: string;
  assigned_to_user_id?: number;
  status?: string;
  priority?: string;
  is_shared?: boolean;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

interface CreateTaskData {
  title: string;
  description?: string;
  matter_id?: number;
  assigned_to_user_id?: number;
  assigned_to_user_uuid?: string;
  status?: string;
  priority?: string;
  due_date?: string;
  recurrence?: Record<string, any>;
  is_shared?: boolean;
  sharing_settings?: Record<string, any>;
  metadata?: Record<string, any>;
}

/**
 * Legacy/compatibility interface for existing components
 * Accepts old field names (TaskTitle, TaskDescription, TaskDeadline)
 */
interface LegacyCreateTaskData {
  TaskTitle?: string;
  title?: string;
  TaskDescription?: string;
  description?: string;
  TaskDeadline?: string;
  due_date?: string;
  [key: string]: any;
}

/**
 * Convert legacy field names to new API format
 * Supports both old (TaskTitle, TaskDeadline) and new (title, due_date) naming conventions
 */
function normalizeLegacyTaskData(data: any): CreateTaskData {
  const result: any = {
    title: data.title || data.TaskTitle,
    description: data.description || data.TaskDescription,
    due_date: data.due_date || data.TaskDeadline,
    status: data.status || data.Status,
    priority: data.priority || data.Priority,
    matter_id: data.matter_id || data.MatterID || data.CaseID,
    assigned_to_user_id: data.assigned_to_user_id,
    assigned_to_user_uuid: data.assigned_to_user_uuid,
    is_shared: data.is_shared,
    sharing_settings: data.sharing_settings,
    recurrence: data.recurrence,
    metadata: data.metadata || data.Metadata,
  };
  // Pass through matter_uuid if provided (for updating task's linked matter)
  if (data.matter_uuid) {
    result.matter_uuid = data.matter_uuid;
  }
  return result;
}

/**
 * Convert API response format to frontend Task interface format
 * Maps new API field names (id, uuid, title, due_date) to expected field names (TaskID, uuid, TaskTitle, TaskDeadline)
 * UUID is now the primary identifier for all API operations
 * Maps count fields from API response (attachment_count, comment_count, collaborator_count)
 */
function normalizeTaskResponse(apiTask: any): Task {
  // Map collaborators from API response - the new API returns a 'collaborators' array
  const collaborators = apiTask.collaborators || apiTask.Collaborators || [];

  return {
    TaskID: apiTask.TaskID || apiTask.id || '',
    id: apiTask.id || apiTask.TaskID || '',
    uuid: apiTask.uuid || '',
    TaskTitle: apiTask.TaskTitle || apiTask.title || '',
    Title: apiTask.Title || apiTask.title || apiTask.TaskTitle || '',
    TaskDescription: apiTask.TaskDescription || apiTask.description || '',
    Description: apiTask.Description || apiTask.description || apiTask.TaskDescription || '',
    TaskDeadline: apiTask.TaskDeadline || apiTask.due_date || '',
    DueDate: apiTask.DueDate || apiTask.due_date || apiTask.TaskDeadline || '',
    Status: apiTask.Status || apiTask.status || 'todo',
    Priority: apiTask.Priority || apiTask.priority || 'medium',
    CaseID: apiTask.CaseID || apiTask.matter_id,
    CreationDate: apiTask.CreationDate || apiTask.created_at,
    ModificationDate: apiTask.ModificationDate || apiTask.updated_at,
    CompletionDate: apiTask.CompletionDate || apiTask.completed_at,
    UserID: apiTask.UserID || apiTask.assigned_to_user_id,
    UserType: apiTask.UserType,
    AssignedBy: apiTask.AssignedBy || apiTask.last_modified_by_user_id,
    AssignedByName: apiTask.AssignedByName,
    AssignedByUserType: apiTask.AssignedByUserType || '',
    ProjectID: apiTask.ProjectID,
    TeamID: apiTask.TeamID,
    Comment: apiTask.Comment,
    Comments: apiTask.Comments || [],
    Collaborators: collaborators,
    collaborators: collaborators,
    Attachments: apiTask.Attachments || [],
    // Map count fields from new API response
    attachment_count: apiTask.attachment_count,
    comment_count: apiTask.comment_count,
    collaborator_count: apiTask.collaborator_count,
    // Map matter fields from API response
    matter_name: apiTask.matter_name,
    matter: apiTask.matter,
  };
}

/**
 * Fetch all tasks with filters and pagination
 * Uses new REST API endpoint: GET /tasks/list
 */
export const fetchAllTasks = (params: TaskListParams = {}): AppThunk<Promise<FetchTasksResponse>> =>
  async (dispatch) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.matter_id) queryParams.append('matter_id', params.matter_id.toString());
      if (params.matter_uuid) queryParams.append('matter_uuid', params.matter_uuid);
      if (params.assigned_to_user_id) queryParams.append('assigned_to_user_id', params.assigned_to_user_id.toString());
      if (params.status) queryParams.append('status', params.status);
      if (params.priority) queryParams.append('priority', params.priority);
      if (params.is_shared !== undefined) queryParams.append('is_shared', params.is_shared.toString());
      if (params.due_date_from) queryParams.append('due_date_from', params.due_date_from);
      if (params.due_date_to) queryParams.append('due_date_to', params.due_date_to);
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const url = `/tasks/list${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await apis({
        method: "GET",
        url,
      });

      if (response && response.status === 200) {
        const { tasks, page, limit, total_count, total_pages, filter_options = [] } = response.data;
        const normalizedTasks = tasks.map(normalizeTaskResponse);
        dispatch({ type: FETCH_TASKS, payload: normalizedTasks });
        return { tasks: normalizedTasks, page, limit, total_count, total_pages, filter_options };
      } else {
        handleApiError(response, dispatch);
        return { tasks: [], page: 1, limit: 20, total_count: 0, total_pages: 1, filter_options: [] };
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return { tasks: [], page: 1, limit: 20, total_count: 0, total_pages: 1, filter_options: [] };
    }
  };

/**
 * Fetch a single task by ID
 * Uses new REST API endpoint: GET /tasks/task/{task_id}
 */
export const fetchTaskDetails = (taskId: string | number): AppThunk<Promise<Task | null>> => 
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/tasks/task/${taskId}`,
      });

      if (response && response.status === 200) {
        const normalizedTask = response.data;
        dispatch({
          type: FETCH_TASK_DETAILS,
          payload: normalizedTask,
        });
        return normalizedTask;
      } else {
        // handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      // handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * Create a new task
 * Accepts both legacy field names (TaskTitle, TaskDeadline) and new names (title, due_date)
 * Uses new REST API endpoint: POST /tasks/create_task
 */
export const createTask = (data: CreateTaskData | LegacyCreateTaskData): AppThunk<Promise<Task | null>> => 
  async (dispatch) => {
    try {
      const normalizedData = normalizeLegacyTaskData(data);
      const response = await apis({
        method: "POST",
        url: "/tasks/create_task",
        data: normalizedData,
      });

      if (response && response.status === 201) {
        const normalizedTask = normalizeTaskResponse(response.data);
        dispatch({
          type: CREATE_TASK_SUCCESS,
          payload: normalizedTask,
        });
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Task created successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return normalizedTask;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      throw error;
    }
  };

/**
 * Create multiple tasks at once (bulk operation)
 * Supports optional matter_uuid to link all tasks to a specific matter
 * Uses new REST API endpoint: POST /tasks/bulk_create_tasks
 */
export const createBulkTasks = (
  tasks: (CreateTaskData | LegacyCreateTaskData)[],
  matterUuid?: string
): AppThunk<Promise<{ created_count: number; tasks: Task[]; failed_count: number; errors?: any[] } | null>> =>
  async (dispatch) => {
    try {
      if (!Array.isArray(tasks) || tasks.length === 0) {
        return null;
      }

      // Normalize each task in the array
      const normalizedTasks = tasks.map(normalizeLegacyTaskData);

      const payload: any = {
        tasks: normalizedTasks,
      };

      // Add matter UUID if provided (links all tasks to the matter)
      if (matterUuid) {
        payload.matter_uuid = matterUuid;
      }

      const response = await apis({
        method: "POST",
        url: "/tasks/bulk_create_tasks",
        data: payload,
      });

      if (response && response.status === 201 && response.data.success) {
        const createdTasks = (response.data.tasks || []).map(normalizeTaskResponse);

        // Dispatch action for each created task
        createdTasks.forEach((task: any) => {
          dispatch({
            type: CREATE_TASK_SUCCESS,
            payload: task,
          });
        });

        const result = {
          created_count: response.data.created_count,
          tasks: createdTasks,
          failed_count: response.data.failed_count || 0,
          errors: response.data.errors
        };

        if (result.created_count > 0) {
          dispatch(showFeedbackModal({
            modalType: 'success',
            message: `${result.created_count} task${result.created_count !== 1 ? 's' : ''} created successfully.${result.failed_count > 0 ? ` ${result.failed_count} failed.` : ''}`,
            showModal: true,
            duration: 3000
          }));
        }

        return result;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      throw error;
    }
  };

/**
 * Assign a task to a user
 * Overloaded to accept:
 * - assignTask(taskId, userId) - new format
 * - assignTask(taskId, [userId]) - legacy format (array)
 * Uses new REST API endpoint: PUT /tasks/task/{task_id}
 */
export const assignTask = (
  taskId: string | number,
  assignedToUserId?: number | number[],
  assignedToUserUuid?: string
): AppThunk<Promise<Task | null>> => 
  async (dispatch) => {
    try {
      const data: any = {};

      // Handle both array format (legacy) and single value format
      if (Array.isArray(assignedToUserId) && assignedToUserId.length > 0) {
        data.assigned_to_user_id = assignedToUserId[0];
      } else if (assignedToUserId && !Array.isArray(assignedToUserId)) {
        data.assigned_to_user_id = assignedToUserId;
      }

      if (assignedToUserUuid) data.assigned_to_user_uuid = assignedToUserUuid;

      const response = await apis({
        method: "PUT",
        url: `/tasks/task/${taskId}`,
        data,
      });

      if (response && response.status === 200) {
        const normalizedTask = normalizeTaskResponse(response.data);
        dispatch({
          type: ASSIGN_TASK_SUCCESS,
          payload: normalizedTask,
        });
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Task assigned successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return normalizedTask;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      throw error;
    }
  };

/**
 * Update a task
 * Overloaded to accept either:
 * - updateTask(taskId, data) - new format
 * - updateTask({TaskID, ...fields}) - legacy format
 * Uses new REST API endpoint: PUT /tasks/task/{task_id}
 */
export const updateTask = (
  taskIdOrData: string | number | any,
  data?: Partial<CreateTaskData | LegacyCreateTaskData>
): AppThunk<Promise<Task | null>> => 
  async (dispatch) => {
    try {
      // Handle both legacy format (data as first param) and new format (taskId, data)
      let taskId: string | number;
      let updateData: any;

      if (typeof taskIdOrData === 'object' && (taskIdOrData.TaskID || taskIdOrData.id)) {
        // Legacy format: updateTask({TaskID: 1, TaskTitle: "..."})
        taskId = taskIdOrData.TaskID || taskIdOrData.id;
        updateData = taskIdOrData;
      } else {
        // New format: updateTask(1, {title: "..."})
        taskId = taskIdOrData;
        updateData = data || {};
      }

      const normalizedData = normalizeLegacyTaskData(updateData);

      const response = await apis({
        method: "PUT",
        url: `/tasks/task/${taskId}`,
        data: normalizedData,
      });

      if (response && response.status === 200) {
        const normalizedTask = normalizeTaskResponse(response.data);
        dispatch({
          type: UPDATE_TASK_SUCCESS,
          payload: normalizedTask,
        });
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Task updated successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return normalizedTask;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * Complete a task (mark as completed)
 * Accepts optional comment parameter for legacy compatibility
 * Uses new REST API endpoint: PUT /tasks/task/{task_id}/complete
 */
export const completeTask = (taskId: string | number, comment?: string): AppThunk<Promise<Task | null>> => 
  async (dispatch) => {
    try {
      const response = await apis({
        method: "PUT",
        url: `/tasks/task/${taskId}/complete`,
      });

      if (response && response.status === 200) {
        const normalizedTask = normalizeTaskResponse(response.data);
        dispatch({
          type: COMPLETE_TASK_SUCCESS,
          payload: normalizedTask,
        });
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: translate("Task completed successfully."),
          showModal: true,
          duration: 3000
        }));
        return normalizedTask;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * Archive a task (soft delete)
 * Uses new REST API endpoint: PUT /tasks/task/{task_uuid}/archive
 */
export const archiveTask = (taskId: string | number): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "PUT",
        url: `/tasks/task/${taskId}/archive`,
      });

      if (response && response.status === 200) {
        dispatch({
          type: DELETE_TASK_SUCCESS,
          payload: taskId,
        });
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: translate("Task archived successfully."),
          showModal: true,
          duration: 3000
        }));
        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return false;
    }
  };

/**
 * Restore an archived task
 * Sets the task status back to todo
 * Uses new REST API endpoint: PUT /tasks/task/{task_uuid}
 */
export const restoreTask = (taskId: string | number): AppThunk<Promise<Task | null>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "PUT",
        url: `/tasks/task/${taskId}`,
        data: { status: 'todo' },
      });

      if (response && response.status === 200) {
        const normalizedTask = normalizeTaskResponse(response.data);
        dispatch({
          type: UPDATE_TASK_SUCCESS,
          payload: normalizedTask,
        });
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: translate("Task restored successfully."),
          showModal: true,
          duration: 3000
        }));
        return normalizedTask;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * Delete a task (soft delete by default)
 * Uses new REST API endpoint: DELETE /tasks/task/{task_id}
 */
export const deleteTask = (taskId: string | number, hardDelete?: boolean): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const url = hardDelete ? `/tasks/task/${taskId}?hard_delete=true` : `/tasks/task/${taskId}`;

      const response = await apis({
        method: "DELETE",
        url,
      });

      if (response && response.status === 200) {
        dispatch({
          type: DELETE_TASK_SUCCESS,
          payload: taskId,
        });
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: translate("Task deleted successfully."),
          showModal: true,
          duration: 3000
        }));
        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return false;
    }
  };

/**
 * Bulk delete multiple tasks by UUIDs
 * Uses REST API endpoint: POST /tasks/bulk_delete
 */
export const bulkDeleteTasks = (taskUuids: string[], hardDelete?: boolean): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "POST",
        url: "/tasks/bulk_delete",
        data: { task_uuids: taskUuids, hard_delete: hardDelete || false },
      });

      if (response && response.status === 200) {
        taskUuids.forEach(id => {
          dispatch({ type: DELETE_TASK_SUCCESS, payload: id });
        });
        const deleted = response.data?.deleted_count || taskUuids.length;
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: `${deleted} task${deleted !== 1 ? 's' : ''} deleted successfully.`,
          showModal: true,
          duration: 3000,
        }));
        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return false;
    }
  };

/**
 * Bulk archive multiple tasks by UUIDs
 * Uses REST API endpoint: POST /tasks/bulk_archive
 */
export const bulkArchiveTasks = (taskUuids: string[]): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "POST",
        url: "/tasks/bulk_archive",
        data: { task_uuids: taskUuids },
      });

      if (response && response.status === 200) {
        taskUuids.forEach(id => {
          dispatch({ type: DELETE_TASK_SUCCESS, payload: id });
        });
        const archived = response.data?.archived_count || taskUuids.length;
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: `${archived} task${archived !== 1 ? 's' : ''} archived successfully.`,
          showModal: true,
          duration: 3000,
        }));
        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return false;
    }
  };

/**
 * Get task preferences (legacy endpoint - kept for backward compatibility)
 */
export const getTaskPreferences = (): AppThunk<Promise<any>> => 
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: "/tasks/preferences",
      });

      if (response && response.status === 200) {
        dispatch({
          type: FETCH_TASK_PREFERENCES,
          payload: response.data,
        });
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * Update task preferences (legacy endpoint - kept for backward compatibility)
 */
export const updateTaskPreferences = (preferences: Record<string, boolean>): AppThunk<Promise<any>> => 
  async (dispatch) => {
    try {
      const response = await apis({
        method: "POST",
        url: "/tasks/preferences",
        data: { preferences },
      });

      if (response && response.status === 200) {
        dispatch({
          type: UPDATE_TASK_PREFERENCES,
          payload: preferences,
        });
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * List comments for a task with pagination
 * Uses new REST API endpoint: GET /tasks/task/{task_uuid}/comments
 */
export const listTaskComments = (
  taskId: string | number,
  page: number = 1,
  limit: number = 50
): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/tasks/task/${taskId}/comments?page=${page}&limit=${limit}`,
      });

      if (response && response.status === 200) {
        return response.data;
      } else {
        // handleApiError(response, dispatch);
        return { comments: [], page, limit, total_count: 0, total_pages: 1 };
      }
    } catch (error: any) {
      // handleApiError(error, dispatch);
      return { comments: [], page, limit, total_count: 0, total_pages: 1 };
    }
  };

/**
 * Add a comment to a task
 * Uses new REST API endpoint: POST /tasks/task/{task_uuid}/comments
 */
export const addTaskComment = (taskId: string | number, commentText: string, isPinned?: boolean): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "POST",
        url: `/tasks/task/${taskId}/comments`,
        data: {
          comment_text: commentText,
          is_pinned: isPinned || false
        },
      });

      if (response && (response.status === 200 || response.status === 201)) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Comment added successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * List attachments for a task
 * Uses new REST API endpoint: GET /tasks/task/{task_uuid}/attachments
 */
export const listTaskAttachments = (taskId: string | number): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/tasks/task/${taskId}/attachments`,
      });

      if (response && response.status === 200) {
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return { attachments: [] };
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return { attachments: [] };
    }
  };

/**
 * Upload an attachment to a task
 * Sends file via multipart/form-data to the endpoint: POST /tasks/task/{task_uuid}/attachments
 * Note: Returns Promise without showing feedback modal (caller handles it)
 */
export const uploadTaskAttachment = (taskId: string | number, file: File): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apis({
        method: "POST",
        url: `/tasks/task/${taskId}/attachments`,
        data: formData,
      });

      if (response && (response.status === 200 || response.status === 201)) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Attachment uploaded successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return response.data;
      } else {
        handleApiError(response, dispatch);
        throw new Error(`Failed to upload attachment: ${response?.status}`);
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      throw error;
    }
  };

/**
 * View an attachment from a task (display inline)
 * Implements streaming of encrypted file content
 * Uses REST API endpoint: GET /tasks/task/{task_uuid}/attachments/{attachment_id}/view
 * Returns blob and metadata for displaying in a viewer
 * Includes comprehensive logging to debug streaming response
 */
export const viewTaskAttachment = (
  taskId: string | number,
  attachmentId: string,
  filename: string
): AppThunk<Promise<{ success: boolean; blob?: Blob; contentType?: string; filename?: string; error?: string }>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/tasks/task/${taskId}/attachments/${attachmentId}/view`,
        responseType: 'blob'
      });


      if (!response) {
        console.error('[viewTaskAttachment] No response received from server');
        return {
          success: false,
          error: 'No response from server'
        };
      }


      if (response.status !== 200) {
        return {
          success: false,
          error: `Server returned status ${response.status}`
        };
      }

      // Validate blob exists and is valid
      const blob = response.data;

      if (!blob || !(blob instanceof Blob)) {
        return {
          success: false,
          error: 'Invalid file content received from server'
        };
      }

      // Check blob size - empty blobs indicate an error
      if (blob.size === 0) {
        return {
          success: false,
          error: 'File is empty or could not be retrieved'
        };
      }

      // Check if server returned an HTML error page instead of the file
      // This commonly happens when auth fails, server errors, etc.
      if (blob.type === 'text/html' || blob.type === '') {
        return {
          success: false,
          error: 'Server returned an error. File may not exist or access was denied.'
        };
      }

      // Get the actual content-type from response headers
      const contentType = ((response.headers['content-type'] as string) || '').toLowerCase().trim() || 'application/octet-stream';

      // Get filename from Content-Disposition header or use provided
      let viewFilename = filename;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
        if (filenameMatch) {
          viewFilename = filenameMatch[1]?.replace(/['"]/g, '') || filename;
        }
      }


      return {
        success: true,
        blob,
        contentType,
        filename: viewFilename
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Error viewing attachment'
      };
    }
  };

/**
 * Trigger browser download of a blob with specified filename
 * Cross-browser compatible helper function
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Download an attachment from a task with streaming support
 * Uses REST API endpoint: GET /tasks/task/{task_uuid}/attachments/{attachment_id}/download
 * Returns blob that is saved to disk
 * Includes comprehensive logging to debug streaming response
 */
export const downloadTaskAttachment = (taskId: string | number, attachmentId: string, filename: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/tasks/task/${taskId}/attachments/${attachmentId}/download`,
        responseType: 'blob'
      });


      if (!response) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('No response from server'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }


      if (response.status !== 200) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: `Server returned status ${response.status}`,
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      // Validate blob exists and is valid
      const blob = response.data;

      if (!blob || !(blob instanceof Blob)) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('Invalid file content received from server'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      // Check blob size - empty blobs indicate an error
      if (blob.size === 0) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('File is empty or could not be retrieved'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      // Check if server returned an HTML error page instead of the file
      if (blob.type === 'text/html' || blob.type === '') {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: translate('Server returned an error. File may not exist or access was denied.'),
          showModal: true,
          duration: 3000
        }));
        return false;
      }

      // Get filename from Content-Disposition header or use provided
      const disposition = response.headers['content-disposition'];
      let downloadFilename = filename;
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\n]*=(?:(['"]).*?\1|[^;\n]*)/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1]?.replace(/['"]/g, '') || filename;
        }
      }


      triggerBlobDownload(blob, downloadFilename);
      return true;
    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: error.message || 'Error downloading attachment',
        showModal: true,
        duration: 3000
      }));
      return false;
    }
  };

/**
 * Remove an attachment from a task
 * Uses new REST API endpoint: DELETE /tasks/task/{task_uuid}/attachments/{attachment_id}
 */
export const removeTaskAttachment = (taskId: string | number, attachmentId: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "DELETE",
        url: `/tasks/task/${taskId}/attachments/${attachmentId}`,
      });

      if (response && response.status === 200) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Attachment removed successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return false;
    }
  };

/**
 * List collaborators for a task
 * Uses new REST API endpoint: GET /tasks/task/{task_uuid}/collaborators
 */
export const listTaskCollaborators = (taskId: string | number): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/tasks/task/${taskId}/collaborators`,
      });

      if (response && response.status === 200) {
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return { collaborators: [] };
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return { collaborators: [] };
    }
  };

/**
 * Add a collaborator to a task
 * Accepts either user ID or user UUID
 * Uses new REST API endpoint: POST /tasks/task/{task_uuid}/collaborators
 */
export const addTaskCollaborator = (
  taskId: string | number,
  collaboratorIdOrEmail: string,
  collaboratorUuid?: string,
  access?: 'edit' | 'view' | 'admin'
): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const data: any = {};

      // Try to determine if it's a UUID, numeric ID, or email
      if (!isNaN(Number(collaboratorIdOrEmail))) {
        // It's a numeric ID (legacy)
        data.collaborator_user_id = Number(collaboratorIdOrEmail);
      } else if (collaboratorIdOrEmail.includes('@')) {
        // It's an email - pass as UUID for backend to resolve
        // (emails are not UUIDs, so backend needs to handle email lookup)
        data.collaborator_uuid = collaboratorIdOrEmail;
      } else {
        // Assume it's a UUID (UUID format from org-people API)
        data.collaborator_uuid = collaboratorIdOrEmail;
      }

      // collaboratorUuid parameter can explicitly override
      if (collaboratorUuid) {
        data.collaborator_uuid = collaboratorUuid;
      }

      // Add access level if provided
      if (access) {
        data.access = access;
      }

      const response = await apis({
        method: "POST",
        url: `/tasks/task/${taskId}/collaborators`,
        data,
      });

      if (response && (response.status === 200 || response.status === 201)) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Collaborator added successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * Remove a collaborator from a task
 * Uses new REST API endpoint: DELETE /tasks/task/{task_uuid}/collaborators/{collaborator_uuid}
 */
export const removeTaskCollaborator = (taskId: string | number, collaboratorUuid: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "DELETE",
        url: `/tasks/task/${taskId}/collaborators/${collaboratorUuid}`,
      });

      if (response && response.status === 200) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Collaborator removed successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return false;
    }
  };

/**
 * Update a collaborator's privilege on a task.
 *
 * The task collaborator API has no update endpoint — only POST (add), DELETE
 * (remove), GET (list). We emulate update by deleting and re-adding with the
 * new access level.
 */
export const updateCollaboratorPrivilege = (
  taskId: string | number,
  collaboratorUuid: string,
  privilege: string | boolean
): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const privilegeValue = typeof privilege === 'boolean'
        ? (privilege ? 'Write' : 'Read')
        : privilege;

      const accessLevel = (
        privilegeValue === 'Write' ? 'edit'
        : privilegeValue === 'Read' ? 'view'
        : privilegeValue
      ) as 'edit' | 'view' | 'admin';

      const removeResp = await apis({
        method: 'DELETE',
        url: `/tasks/task/${taskId}/collaborators/${collaboratorUuid}`,
      });
      if (!removeResp || (removeResp.status !== 200 && removeResp.status !== 204)) {
        handleApiError(removeResp, dispatch);
        return null;
      }

      const addResp = await apis({
        method: 'POST',
        url: `/tasks/task/${taskId}/collaborators`,
        data: { collaborator_uuid: collaboratorUuid, access: accessLevel },
      });
      if (addResp && (addResp.status === 200 || addResp.status === 201)) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: 'Access level updated successfully.',
        //   showModal: true,
        //   duration: 3000,
        // }));
        return addResp.data;
      }
      handleApiError(addResp, dispatch);
      return null;
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * List related items for a task
 * Uses new REST API endpoint: GET /tasks/task/{task_uuid}/related-items
 */
export const listTaskRelatedItems = (taskId: string | number): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "GET",
        url: `/tasks/task/${taskId}/related-items`,
      });

      if (response && response.status === 200) {
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return { related_items: [] };
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return { related_items: [] };
    }
  };

/**
 * Add a related item to a task
 * Uses new REST API endpoint: POST /tasks/task/{task_uuid}/related-items
 */
export const addTaskRelatedItem = (
  taskId: string | number,
  relatedItemType: string,
  relatedItemId: string | number,
  relationshipType?: string
): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const payload: any = {
        related_item_type: relatedItemType,
        relationship_type: relationshipType || 'related_to'
      };

      if (relatedItemType === 'matter') {
        payload.matter_uuid = relatedItemId;
      } else {
        payload.related_item_id = relatedItemId;
      }

      const response = await apis({
        method: "POST",
        url: `/tasks/task/${taskId}/related-items`,
        data: payload,
      });

      if (response && (response.status === 200 || response.status === 201)) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Related item added successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return response.data;
      } else {
        handleApiError(response, dispatch);
        return null;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return null;
    }
  };

/**
 * Remove a related item from a task
 * Uses new REST API endpoint: DELETE /tasks/task/{task_uuid}/related-items/{related_item_uuid}
 */
export const removeTaskRelatedItem = (
  taskId: string | number,
  relatedItemUuid: string
): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const response = await apis({
        method: "DELETE",
        url: `/tasks/task/${taskId}/related-items/${relatedItemUuid}`,
      });

      if (response && response.status === 200) {
        // dispatch(showFeedbackModal({
        //   modalType: 'success',
        //   message: "Related item removed successfully.",
        //   showModal: true,
        //   duration: 3000
        // }));
        return true;
      } else {
        handleApiError(response, dispatch);
        return false;
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return false;
    }
  };

// ─── Task Feed Actions (SQL-backed) ────────────────────────────────────────────

export const fetchTaskAuditLog = (
  taskUuid: string,
  page: number = 1,
  limit: number = 50,
): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis.get(`/tasks/${taskUuid}/audit-log`, {
        params: { page, limit },
      });
      if (response?.data?.success || response?.status === 200) {
        return response.data;
      }
      return { entries: [], pagination: {} };
    } catch (error: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to load activity log',
        modalType: 'error',
        duration: 3000,
      }));
      return { entries: [], pagination: {} };
    }
  };

export const fetchTaskFeed = (taskUuid: string): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis.get(`/tasks/task/${taskUuid}/feed`);
      if (response?.data?.success || response?.status === 200) {
        return response.data.feed || [];
      }
      return [];
    } catch (error: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to load feed',
        modalType: 'error',
        duration: 3000,
      }));
      return [];
    }
  };

export const postTaskFeedEvent = (
  taskUuid: string,
  eventData: { event_type: string; message: string; metadata?: any }
): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis.post(`/tasks/task/${taskUuid}/feed`, eventData);
      if (response?.data?.success || response?.status === 200 || response?.status === 201) {
        return response.data.event || response.data;
      }
      throw new Error('Failed to post event');
    } catch (error: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to post event',
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  };

export const addTaskFeedComment = (
  taskUuid: string,
  eventId: string | number,
  message: string
): AppThunk<Promise<any>> =>
  async (dispatch) => {
    try {
      const response = await apis.post(`/tasks/task/${taskUuid}/feed/${eventId}/comments`, { message });
      if (response?.data?.success || response?.status === 200 || response?.status === 201) {
        return response.data.comment || response.data;
      }
      throw new Error('Failed to add comment');
    } catch (error: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.message || 'Failed to add comment',
        modalType: 'error',
        duration: 3000,
      }));
      return null;
    }
  };
